export type GradeLevel =
  | 'K' |'Grade 1' |'Grade 2' |'Grade 3' |'Grade 4' |'Grade 5' |'Grade 6' |'Grade 7' |'Grade 8';

export type WorksheetStatus = 'Published' | 'Draft' | 'Archived';

export type CategoryName =
  | 'Addition' |'Subtraction' |'Multiplication' |'Division' |'Fractions' |'Geometry' |'Algebra' |'Word Problems';

export interface Category {
  id: string;
  name: CategoryName;
  color: string;
  textColor: string;
  count: number;
  description: string;
}

export interface Worksheet {
  id: string;
  title: string;
  category: CategoryName;
  grade: GradeLevel;
  pages: number;
  downloads: number;
  status: WorksheetStatus;
  uploadedAt: string;
  description: string;
  fileSize: string;
}

export const CATEGORIES: Category[] = [
  {
    id: 'cat-addition',
    name: 'Addition',
    color: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    count: 14,
    description: 'Single and multi-digit addition problems',
  },
  {
    id: 'cat-subtraction',
    name: 'Subtraction',
    color: 'bg-amber-100',
    textColor: 'text-amber-700',
    count: 11,
    description: 'Borrowing, regrouping, and mental math',
  },
  {
    id: 'cat-multiplication',
    name: 'Multiplication',
    color: 'bg-indigo-100',
    textColor: 'text-indigo-700',
    count: 18,
    description: 'Times tables, arrays, and long multiplication',
  },
  {
    id: 'cat-division',
    name: 'Division',
    color: 'bg-red-100',
    textColor: 'text-red-700',
    count: 9,
    description: 'Short division, remainders, and long division',
  },
  {
    id: 'cat-fractions',
    name: 'Fractions',
    color: 'bg-pink-100',
    textColor: 'text-pink-700',
    count: 13,
    description: 'Equivalent fractions, adding and comparing',
  },
  {
    id: 'cat-geometry',
    name: 'Geometry',
    color: 'bg-teal-100',
    textColor: 'text-teal-700',
    count: 7,
    description: 'Shapes, area, perimeter, and angles',
  },
  {
    id: 'cat-algebra',
    name: 'Algebra',
    color: 'bg-violet-100',
    textColor: 'text-violet-700',
    count: 10,
    description: 'Variables, expressions, and simple equations',
  },
  {
    id: 'cat-word-problems',
    name: 'Word Problems',
    color: 'bg-orange-100',
    textColor: 'text-orange-700',
    count: 8,
    description: 'Real-world multi-step math scenarios',
  },
];

export const WORKSHEETS: Worksheet[] = [
  {
    id: 'ws-001',
    title: 'Two-Digit Addition with Carrying',
    category: 'Addition',
    grade: 'Grade 2',
    pages: 2,
    downloads: 3842,
    status: 'Published',
    uploadedAt: 'May 10, 2026',
    description: 'Practice two-digit addition problems that require carrying over to the tens column.',
    fileSize: '1.2 MB',
  },
  {
    id: 'ws-002',
    title: 'Multiplication Tables 1–12',
    category: 'Multiplication',
    grade: 'Grade 3',
    pages: 4,
    downloads: 5210,
    status: 'Published',
    uploadedAt: 'May 8, 2026',
    description: 'Complete reference and practice sheets for all multiplication tables from 1 to 12.',
    fileSize: '2.1 MB',
  },
  {
    id: 'ws-003',
    title: 'Identifying Equivalent Fractions',
    category: 'Fractions',
    grade: 'Grade 4',
    pages: 3,
    downloads: 2967,
    status: 'Published',
    uploadedAt: 'May 6, 2026',
    description: 'Visual and numerical exercises to identify and generate equivalent fractions.',
    fileSize: '1.8 MB',
  },
  {
    id: 'ws-004',
    title: 'Area and Perimeter of Rectangles',
    category: 'Geometry',
    grade: 'Grade 4',
    pages: 2,
    downloads: 1854,
    status: 'Published',
    uploadedAt: 'May 5, 2026',
    description: 'Calculate area and perimeter for rectangles and composite shapes.',
    fileSize: '0.9 MB',
  },
  {
    id: 'ws-005',
    title: 'Subtraction with Borrowing',
    category: 'Subtraction',
    grade: 'Grade 2',
    pages: 2,
    downloads: 2441,
    status: 'Published',
    uploadedAt: 'May 3, 2026',
    description: 'Three-digit subtraction problems requiring borrowing across columns.',
    fileSize: '1.1 MB',
  },
  {
    id: 'ws-006',
    title: 'Long Division Practice',
    category: 'Division',
    grade: 'Grade 5',
    pages: 3,
    downloads: 1622,
    status: 'Published',
    uploadedAt: 'May 1, 2026',
    description: 'Step-by-step long division problems with and without remainders.',
    fileSize: '1.5 MB',
  },
  {
    id: 'ws-007',
    title: 'Solving One-Step Equations',
    category: 'Algebra',
    grade: 'Grade 6',
    pages: 2,
    downloads: 1398,
    status: 'Published',
    uploadedAt: 'Apr 29, 2026',
    description: 'Introduction to solving equations with one variable using inverse operations.',
    fileSize: '0.8 MB',
  },
  {
    id: 'ws-008',
    title: 'Word Problems: Money and Change',
    category: 'Word Problems',
    grade: 'Grade 3',
    pages: 2,
    downloads: 2105,
    status: 'Published',
    uploadedAt: 'Apr 27, 2026',
    description: 'Real-world word problems involving counting money, making change, and budgeting.',
    fileSize: '1.0 MB',
  },
  {
    id: 'ws-009',
    title: 'Counting and Skip Counting by 2s, 5s, 10s',
    category: 'Addition',
    grade: 'Grade 1',
    pages: 2,
    downloads: 3120,
    status: 'Published',
    uploadedAt: 'Apr 25, 2026',
    description: 'Number line and fill-in exercises for skip counting patterns.',
    fileSize: '0.7 MB',
  },
  {
    id: 'ws-010',
    title: 'Adding and Subtracting Fractions',
    category: 'Fractions',
    grade: 'Grade 5',
    pages: 3,
    downloads: 1789,
    status: 'Published',
    uploadedAt: 'Apr 22, 2026',
    description: 'Mixed practice adding and subtracting fractions with unlike denominators.',
    fileSize: '1.4 MB',
  },
  {
    id: 'ws-011',
    title: 'Angles: Acute, Obtuse, and Right',
    category: 'Geometry',
    grade: 'Grade 5',
    pages: 2,
    downloads: 987,
    status: 'Published',
    uploadedAt: 'Apr 20, 2026',
    description: 'Identify and measure angles using a protractor. Classify by type.',
    fileSize: '1.6 MB',
  },
  {
    id: 'ws-012',
    title: 'Two-Step Word Problems: Grade 4',
    category: 'Word Problems',
    grade: 'Grade 4',
    pages: 3,
    downloads: 1543,
    status: 'Published',
    uploadedAt: 'Apr 18, 2026',
    description: 'Multi-step word problems requiring addition, subtraction, and multiplication.',
    fileSize: '1.2 MB',
  },
  {
    id: 'ws-013',
    title: 'Multiplication by 10, 100, and 1000',
    category: 'Multiplication',
    grade: 'Grade 4',
    pages: 2,
    downloads: 2234,
    status: 'Published',
    uploadedAt: 'Apr 15, 2026',
    description: 'Pattern-based exercises for multiplying whole numbers by powers of 10.',
    fileSize: '0.6 MB',
  },
  {
    id: 'ws-014',
    title: 'Introduction to Variables',
    category: 'Algebra',
    grade: 'Grade 7',
    pages: 4,
    downloads: 876,
    status: 'Draft',
    uploadedAt: 'Apr 12, 2026',
    description: 'Understanding variables as unknowns, writing expressions from word descriptions.',
    fileSize: '1.9 MB',
  },
  {
    id: 'ws-015',
    title: 'Kindergarten Number Recognition 1–20',
    category: 'Addition',
    grade: 'K',
    pages: 2,
    downloads: 4102,
    status: 'Published',
    uploadedAt: 'Apr 10, 2026',
    description: 'Trace, count, and match numbers from 1 to 20 with pictures.',
    fileSize: '2.4 MB',
  },
  {
    id: 'ws-016',
    title: 'Dividing Fractions',
    category: 'Fractions',
    grade: 'Grade 6',
    pages: 3,
    downloads: 654,
    status: 'Archived',
    uploadedAt: 'Mar 28, 2026',
    description: 'Dividing fractions and mixed numbers using the keep-change-flip method.',
    fileSize: '1.3 MB',
  },
];

export const DOWNLOAD_TREND = [
  { month: 'Dec', downloads: 1840 },
  { month: 'Jan', downloads: 2210 },
  { month: 'Feb', downloads: 1970 },
  { month: 'Mar', downloads: 2650 },
  { month: 'Apr', downloads: 3120 },
  { month: 'May', downloads: 2890 },
];

export const DOWNLOADS_BY_CATEGORY = [
  { category: 'Addition', downloads: 11064 },
  { category: 'Multiplication', downloads: 7444 },
  { category: 'Subtraction', downloads: 2441 },
  { category: 'Fractions', downloads: 5410 },
  { category: 'Word Problems', downloads: 3648 },
  { category: 'Division', downloads: 1622 },
  { category: 'Geometry', downloads: 2841 },
  { category: 'Algebra', downloads: 2274 },
];

export function getCategoryMeta(name: CategoryName) {
  return CATEGORIES.find((c) => c.name === name)!;
}